import { ArticleStatus, MarketPlace, ProductCardStatus } from "@/api/types/main/schemas";
import { MpstatIcon } from "@/assets/mpstat.icon";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { getMarketplaceIcons } from "@/lib/utils";
import { Share } from "lucide-react";
import { FC, memo, useCallback } from "react";
import { toast } from "sonner";
import { CopyButton } from "../copy.button";

const PLACEHOLDER = "—";

const SHARE_BASE_URL =
  import.meta.env.VITE_FRONT_ENVIRONMENT === "dev"
    ? import.meta.env.VITE_DEV_BACK_API
    : import.meta.env.VITE_FRONT_ENVIRONMENT === "prod"
      ? import.meta.env.VITE_PROD_BACK_API
      : "http://localhost:3000";

interface ProductInfoProps {
  parentRoute: string;
  productId: number;
  name?: string;
  sku?: number;
  generalArticle?: number;
  image?: string;
  url?: string;
  mpstatLink?: string;
  marketplace?: MarketPlace;
  productCardStatus?: ProductCardStatus;
  articleStatus?: ArticleStatus;
}

async function copyText(text: string, successMessage: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast(successMessage);
  } catch (error) {
    console.error(error);
    toast("Не удалось скопировать ссылку");
  }
}

export const ProductInfo: FC<ProductInfoProps> = memo(function ProductInfo({
  parentRoute,
  productId,
  name,
  sku,
  generalArticle,
  image,
  url,
  mpstatLink,
  marketplace,
}) {
  const displayName = name?.trim() || PLACEHOLDER;
  const cardUrl = `${SHARE_BASE_URL}${parentRoute}/${productId}`;

  const handleShare = useCallback(
    () => copyText(cardUrl, "Ссылка скопирована в буфер обмена"),
    [cardUrl]
  );

  const hasMeta = generalArticle != null || sku != null;

  return (
    <div className="flex w-full min-w-0 items-center gap-x-2 2xl:gap-x-4">
      <Avatar className="h-12 w-12 flex-shrink-0 rounded">
        {image && <AvatarImage src={image} alt={displayName} className="rounded" />}
        <AvatarFallback className="rounded text-xs">{displayName?.charAt(0) || "-"}</AvatarFallback>
      </Avatar>

      <div className="flex w-full min-w-0 flex-col 2xl:gap-y-1">
        <div className="flex w-full min-w-0 items-center">
          <div className="flex min-w-0 flex-shrink gap-x-1">
            <span className="min-w-0 flex-grow truncate" title={displayName}>
              {displayName}
            </span>
            {name && <CopyButton text={name} />}
          </div>

          <div className="ml-auto flex flex-shrink-0 gap-x-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Скопировать ссылку на карточку"
              title="Скопировать ссылку на карточку"
              className="h-5 w-5 rounded text-primary duration-100 hover:text-primary"
              onClick={handleShare}
            >
              <Share />
            </Button>

            {marketplace && url && (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Открыть на маркетплейсе"
                onClick={(e) => e.stopPropagation()}
              >
                {getMarketplaceIcons(marketplace)}
              </a>
            )}

            {mpstatLink && (
              <a
                href={mpstatLink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Открыть в MPStat"
                onClick={(e) => e.stopPropagation()}
              >
                <MpstatIcon />
              </a>
            )}
          </div>
        </div>

        {hasMeta && (
          <div className="flex min-w-0 items-center gap-x-2 font-medium text-muted-foreground">
            {generalArticle != null && (
              <div className="flex min-w-0 flex-shrink gap-x-1">
                <span className="min-w-0 flex-grow truncate">{generalArticle}</span>
                <CopyButton text={String(generalArticle)} />
              </div>
            )}
            {sku != null && (
              <div className="flex min-w-0 flex-shrink gap-x-1">
                <span className="min-w-0 flex-grow truncate">{sku}</span>
                <CopyButton text={String(sku)} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});
